import React, { useEffect } from "react";
import { FieldArray, useFormikContext } from "formik";
import AsyncFormikDropdown from "../../components/inputFields/AsyncFormikDropDown";
import InputField from "../../components/inputFields/InputField";
import { TiDeleteOutline } from "react-icons/ti";
import StyledHeadingText from "../../components/Fonts/StyledHeadingText";
import { CgAdd } from "react-icons/cg";
import { motion, AnimatePresence } from "framer-motion";
import { getUserRole } from "../../helpers/local_storage_helpers";

const ProductsForm = ({ searchProductApi, compactMode = false }) => {
    const { values, setFieldValue } = useFormikContext();
    const isStaff = getUserRole() === "staff";

    useEffect(() => {
        const updatedItems = values.items.map((item) => {
            const qty = item.quantity || 0;
            const price = item.price || 0;
            const tax = item.tax_percent || 0;
            const isTaxedPrice = item.is_taxed_price === true;

            let total = qty * price;
            if (!isTaxedPrice) {
                total += total * (tax / 100);
            }

            return {
                ...item,
                total_price: total,
            };
        });

        const subtotal = updatedItems.reduce((sum, item) => sum + item.total_price, 0);
        const discountAmount = (values.discount || 0);
        const deliveryCharge = values.delivery_charge || 0;
        const totalAmount = subtotal - discountAmount + deliveryCharge;
        const totalItems = updatedItems.reduce((count, item) => count + (item.quantity || 0), 0);

        if (JSON.stringify(values.items) !== JSON.stringify(updatedItems)) {
            setFieldValue("items", updatedItems, false);
        }
        setFieldValue("subtotal", subtotal, false);
        setFieldValue("discount_amount", discountAmount, false); // Add this to track discount amount
        setFieldValue("amount", totalAmount, false);
        setFieldValue("total_items", totalItems, false);
    }, [values.items, values.discount, values.delivery_charge, setFieldValue]);

    const handleQuantityChange = (index, value) => {
        const newValue = Math.max(0, parseInt(value) || 0);
        setFieldValue(`items[${index}].quantity`, newValue);
    };

    const handleDiscountChange = (e) => {
        const discount = (e.target.value) || 0;
        setFieldValue("discount", discount);
    };

    const handleDeliveryChargeChange = (e) => {
        const charge = Math.max(0, parseInt(e.target.value) || 0);
        setFieldValue("delivery_charge", charge);
    };

    return (
        <div className="w-full">
            <FieldArray name="items">
                {({ push, remove }) => (
                    <div className={compactMode ? "space-y-2" : "space-y-3"}>
                        {!compactMode && (
                            <StyledHeadingText
                                text="Products"
                                className="text-2xl font-bold text-gray-900"
                            />
                        )}

                        {/* Products Table */}
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-x-auto">
                            {/* Table Header */}
                            <div className="bg-gray-50 border-b border-gray-200 min-w-[800px]">
                                <div className={`grid grid-cols-12 gap-2 ${compactMode ? "px-3 py-2" : "px-4 py-3"}`}>
                                    <div className="col-span-4">
                                        <span className={`${compactMode ? "text-xs" : "text-sm"} font-semibold text-gray-700 uppercase tracking-wide`}>
                                            Product
                                        </span>
                                    </div>
                                    <div className="col-span-2">
                                        <span className={`${compactMode ? "text-xs" : "text-sm"} font-semibold text-gray-700 uppercase tracking-wide`}>
                                            Quantity
                                        </span>
                                    </div>
                                    <div className="col-span-2">
                                        <span className={`${compactMode ? "text-xs" : "text-sm"} font-semibold text-gray-700 uppercase tracking-wide`}>
                                            Price
                                        </span>
                                    </div>
                                    <div className="col-span-1">
                                        <span className={`${compactMode ? "text-xs" : "text-sm"} font-semibold text-gray-700 uppercase tracking-wide`}>
                                            Tax%
                                        </span>
                                    </div>
                                    <div className="col-span-2">
                                        <span className={`${compactMode ? "text-xs" : "text-sm"} font-semibold text-gray-700 uppercase tracking-wide`}>
                                            Total
                                        </span>
                                    </div>
                                    <div className="col-span-1">
                                        <span className={`${compactMode ? "text-xs" : "text-sm"} font-semibold text-gray-700 uppercase tracking-wide`}>
                                            Action
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Table Body */}
                            <div className={`divide-y divide-gray-100 min-w-[800px] ${compactMode ? "max-h-[320px] overflow-y-auto" : ""}`}>
                                <AnimatePresence>
                                    {values.items?.map((product, index) => (
                                        <motion.div
                                            key={index}
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0, x: -50 }}
                                            transition={{ duration: 0.2 }}
                                            className="hover:bg-gray-50 transition-colors"
                                        >
                                            <div className={`grid grid-cols-12 gap-2 items-center ${compactMode ? "px-3 py-2" : "px-4 py-3"}`}>
                                                {/* Product Column */}
                                                <div className="col-span-4">
                                                    {product.product_name ? (
                                                        <div className="flex items-center space-x-3">
                                                            <div className={`${compactMode ? "w-6 h-6" : "w-8 h-8"} bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0`}>
                                                                <span className={`${compactMode ? "text-xs" : "text-sm"} text-blue-600 font-semibold`}>
                                                                    {index + 1}
                                                                </span>
                                                            </div>
                                                            <InputField
                                                                name={`items[${index}].product_name`}
                                                                type="text"
                                                                disabled
                                                                className="flex-1 bg-gray-50 text-gray-800 font-medium"
                                                            />
                                                        </div>
                                                    ) : (
                                                        <div className="flex items-center space-x-3">
                                                            <div className={`${compactMode ? "w-6 h-6" : "w-8 h-8"} bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0`}>
                                                                <span className={`${compactMode ? "text-xs" : "text-sm"} text-gray-500 font-semibold`}>
                                                                    {index + 1}
                                                                </span>
                                                            </div>
                                                            <div className="flex-1 min-w-0">
                                                                <AsyncFormikDropdown
                                                                    name={`items[${index}].product_name`}
                                                                    apiCallFunction={async (input) => {
                                                                        const response = await searchProductApi(input);
                                                                        const results = response.data.results || response.data;
                                                                        return (Array.isArray(results) ? results : []).map((pro) => ({
                                                                            value: pro.id,
                                                                            label: pro.aka_name ?? pro.product_name,
                                                                            price: pro.price,
                                                                        }));
                                                                    }}
                                                                    needSearch
                                                                    className="w-full"
                                                                    placeholder="Search product..."
                                                                    onChange={(selectedOption) => {
                                                                        if (selectedOption) {
                                                                            const existingProductIndex = values.items.findIndex(
                                                                                (pro) => pro.shop_product_id === selectedOption.value
                                                                            );

                                                                            if (existingProductIndex !== -1) {
                                                                                const updatedQuantity =
                                                                                    values.items[existingProductIndex].quantity + 1;
                                                                                setFieldValue(
                                                                                    `items[${existingProductIndex}].quantity`,
                                                                                    updatedQuantity
                                                                                );
                                                                            } else {
                                                                                setFieldValue(`items[${index}].product_name`, selectedOption.label);
                                                                                setFieldValue(`items[${index}].shop_product_id`, selectedOption.value);
                                                                                setFieldValue(`items[${index}].price`, selectedOption.price);
                                                                                setFieldValue(`items[${index}].quantity`, 1);
                                                                                setFieldValue(`items[${index}].tax_percent`, 0);
                                                                            }
                                                                        }
                                                                    }}
                                                                />
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Quantity Column */}
                                                <div className="col-span-2">
                                                    <div className="flex items-center rounded-md bg-white">
                                                        {/* <button
                                                            type="button"
                                                            className="px-2 py-1 text-gray-500 hover:text-red-600 hover:bg-red-50 transition-colors"
                                                            onClick={() => decrementQuantity(index)}
                                                        >
                                                            <FiMinus size={14} />
                                                        </button> */}
                                                        <InputField
                                                            min={0}
                                                            name={`items[${index}].quantity`}
                                                            type="number"
                                                            className="flex-1 text-center border-0 bg-transparent px-0 font-semibold w-12"
                                                            onChange={(e) => handleQuantityChange(index, e.target.value)}
                                                        />
                                                        {/* <button
                                                            type="button"
                                                            className="px-2 py-1 text-gray-500 hover:text-green-600 hover:bg-green-50 transition-colors"
                                                            onClick={() => incrementQuantity(index)}
                                                        >
                                                            <FiPlus size={14} />
                                                        </button> */}
                                                    </div>
                                                </div>

                                                {/* Price Column */}
                                                <div className="col-span-2">
                                                    <div className="relative max-w-[120px]">
                                                        <span className="absolute left-2 top-1/2 transform px-2 pt-1.5 -translate-y-1/2 text-gray-500 text-sm">
                                                            ₹
                                                        </span>
                                                        <InputField
                                                            min={0}
                                                            name={`items[${index}].price`}
                                                            type="number"
                                                            placeholder="0.00"
                                                            step="any"
                                                            className="w-full text-sm pl-3"
                                                        />
                                                    </div>
                                                </div>

                                                {/* Tax % Column */}
                                                <div className="col-span-1">
                                                    <InputField
                                                        name={`items[${index}].tax_percent`}
                                                        type="number"
                                                        min={0}
                                                        max={100}
                                                        placeholder="0"
                                                        step="any"
                                                        className="w-full max-w-[60px] text-center text-sm mx-auto"
                                                    />
                                                </div>

                                                {/* Total Column */}
                                                <div className="col-span-2">
                                                    <div className="pr-2">
                                                        <span className="inline-block bg-green-100 text-green-800 px-2 py-1 rounded text-xs font-semibold">
                                                            ₹ {(product.total_price || 0).toFixed(2)}
                                                        </span>
                                                    </div>
                                                </div>

                                                {/* Extra Fields Row (e.g. dispatch note, B2B tax info) */}
                                                <div className="col-span-12 grid grid-cols-12 gap-2 border-t border-dashed border-gray-100 pt-2 pb-1">
                                                    {values.order_type === "B2B" ? (
                                                        <>
                                                            <div className={isStaff ? "col-span-12 flex items-center gap-2" : "col-span-4 flex items-center gap-2"}>
                                                                <input
                                                                    type="checkbox"
                                                                    id={`items[${index}].is_taxed_price`}
                                                                    checked={product.is_taxed_price === true}
                                                                    onChange={(e) => setFieldValue(`items[${index}].is_taxed_price`, e.target.checked)}
                                                                    className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                                                                />
                                                                <label htmlFor={`items[${index}].is_taxed_price`} className="text-xs font-medium text-gray-700 select-none">
                                                                    Price includes Tax (Taxed Price)
                                                                </label>
                                                            </div>
                                                            {!isStaff && (
                                                                <div className="col-span-8">
                                                                    <input
                                                                        type="text"
                                                                        value={product.dispatch_note || ""}
                                                                        onChange={(e) => setFieldValue(`items[${index}].dispatch_note`, e.target.value)}
                                                                        placeholder="Dispatch Note (e.g. Dispatch from Indore warehouse)"
                                                                        className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-purple-500"
                                                                    />
                                                                </div>
                                                            )}
                                                        </>
                                                    ) : (
                                                        !isStaff && (
                                                            <div className="col-span-12">
                                                                <input
                                                                    type="text"
                                                                    value={product.dispatch_note || ""}
                                                                    onChange={(e) => setFieldValue(`items[${index}].dispatch_note`, e.target.value)}
                                                                    placeholder="Dispatch Note (e.g. Dispatch from Indore warehouse)"
                                                                    className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-purple-500"
                                                                />
                                                            </div>
                                                        )
                                                    )}
                                                </div>
                                            </div>
                                        </motion.div>
                                    ))}
                                </AnimatePresence>

                                {/* Empty State */}
                                {(!values.items || values.items.length === 0) && (
                                    <div className="cursor-pointer px-4 py-12 text-center min-w-[800px]" onClick={() =>
                                        push({
                                            product_name: "",
                                            shop_product_id: "",
                                            quantity: 1,
                                            price: 0,
                                            tax_percent: 0,
                                            total_price: 0,
                                            is_taxed_price: false,
                                            dispatch_note: "",
                                        })
                                    }>
                                        <div className="text-gray-400 mb-4">
                                            <CgAdd size={48} className="mx-auto mb-4 opacity-50" />
                                            <p className="text-lg font-medium text-gray-600">No products added yet</p>
                                            <p className="text-sm text-gray-500">Click "Add Product" to get started</p>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Table Footer - Summary */}
                            {values.items && values.items.length > 0 && (
                                <div className={`bg-gray-50 border-t border-gray-200 min-w-[800px] ${compactMode ? "px-3 py-2" : "px-4 py-3"}`}>
                                    <div className="space-y-3">
                                        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
                                            <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
                                                <div>
                                                    <span className="text-gray-600">Total Items: </span>
                                                    <span className="font-semibold text-gray-900">
                                                        {values.total_items || 0}
                                                    </span>
                                                </div>
                                                <div>
                                                    <span className="text-gray-600">Products: </span>
                                                    <span className="font-semibold text-gray-900">
                                                        {values.items.length}
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="text-right">
                                                <div className="text-sm text-gray-600">
                                                    Subtotal: ₹ {(values.subtotal || 0).toFixed(2)}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Discount Row */}
                                        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 border-t pt-3">
                                            <div className="flex items-center gap-2">
                                                <span className="text-sm font-medium text-gray-700">Discount:</span>
                                                <div className="flex items-center">
                                                    <div className="relative">
                                                        <InputField
                                                            name="discount"
                                                            type="number"
                                                            min={0}
                                                            placeholder="Amount"
                                                            className="w-20 text-center py-1 text-sm border-gray-300 rounded"
                                                            onChange={handleDiscountChange}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="text-sm text-gray-600">
                                                Discount: ₹ {(values.discount_amount || 0)}
                                            </div>
                                        </div>

                                        {/* Delivery Charge Row */}
                                        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 border-t pt-3">
                                            <div className="flex items-center gap-2">
                                                <span className="text-sm font-medium text-gray-700">Delivery Charge:</span>
                                                <div className="flex items-center">
                                                    <div className="relative">
                                                        <InputField
                                                            name="delivery_charge"
                                                            type="number"
                                                            min={0}
                                                            placeholder="0"
                                                            className="w-24 pl-6 text-center py-1 text-sm border-gray-300 rounded"
                                                            onChange={handleDeliveryChargeChange}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="text-sm text-gray-600">
                                                Delivery: ₹ {(values.delivery_charge || 0).toFixed(2)}
                                            </div>
                                        </div>

                                        {/* Total Row */}
                                        <div className="flex justify-between items-center border-t pt-3">
                                            <div className="text-lg font-semibold text-gray-700">
                                                Total:
                                            </div>
                                            <div className="text-xl font-bold text-green-600">
                                                ₹ {(values.amount || 0).toFixed(2)}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Add Product Button */}
                        <motion.button
                            type="button"
                            className={`w-full flex items-center justify-center gap-2 text-primary font-medium rounded-lg border-2 border-dashed border-primary/25 hover:border-primary hover:bg-blue-50 transition-all duration-200 ${compactMode ? "px-4 py-2" : "px-4 py-3"}`}
                            onClick={() =>
                                push({
                                    product_name: "",
                                    shop_product_id: "",
                                    quantity: 1,
                                    price: 0,
                                    tax_percent: 0,
                                    total_price: 0,
                                    is_taxed_price: false,
                                    dispatch_note: "",
                                })
                            }
                            whileHover={{ scale: 1.01 }}
                            whileTap={{ scale: 0.99 }}
                        >
                            <CgAdd size={20} />
                            <span>Add Product</span>
                        </motion.button>
                    </div>
                )}
            </FieldArray>
        </div>
    );
};

export default ProductsForm;